import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCloudUploadAlt,
  faCheckCircle,
  faTimesCircle,
  faUpload,
  faTimes,
  faDownload,
  faFileAlt,
  faHistory,
  faEye,
  faExclamationTriangle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { uploadSettlementBatchSr } from "../../services/settlementApi";

// CSV template columns
const CSV_TEMPLATE_HEADERS = [
  "Payment Ref No",
  "CV No",
  "Check No",
  "Check Amount",
  "CV Date(formerly Credit Date)",
  "Provider O.R. No",
  "Provider O.R. Date",
  "Provider O.R. Amount",
  "Date Deducted",
];

const CSV_COLUMNS = [
  { label: "Payment Ref No", required: true, note: "Unique payment reference number" },
  { label: "CV No", required: true, note: "Check voucher number" },
  { label: "Check No", required: true, note: "Check number" },
  { label: "Check Amount", required: true, note: "Numeric value (commas allowed, e.g. 1,500.00)" },
  { label: "CV Date(formerly Credit Date)", required: true, note: "Format: DD/MM/YYYY" },
  { label: "Provider O.R. No", required: false, note: "Optional" },
  { label: "Provider O.R. Date", required: false, note: "Optional - Format: DD/MM/YYYY" },
  { label: "Provider O.R. Amount", required: false, note: "Optional - Numeric value" },
  { label: "Date Deducted", required: false, note: "Optional - Format: DD/MM/YYYY" },
];

const SettlementBatchUploadPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    const isValidType =
      selectedFile.type === "text/csv" ||
      selectedFile.type === "text/plain" ||
      selectedFile.name.endsWith(".csv");
    if (!isValidType) {
      toast.error("Please select a valid CSV file (.csv)");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("File size exceeds 10 MB limit");
      return;
    }
    setFile(selectedFile);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadSettlementBatchSr(formData);
      const result = response.data;
      setUploadResult({
        success: true,
        uploadId: result.upload_id,
        fileName: result.file_name,
        fileSize: result.file_size,
        message: result.message,
      });
      toast.success("File uploaded successfully. Processing started in background.");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      const errMsg = error.response?.data?.message || error.message || "Upload failed";
      setUploadResult({ success: false, message: errMsg });
      toast.error(typeof errMsg === "string" ? errMsg : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) handleFileSelect({ target: { files: droppedFiles } });
  };

  const handleDownloadTemplate = () => {
    const csvContent = CSV_TEMPLATE_HEADERS.join(",") + "\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "settlement_batch_upload_template.csv");
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Template downloaded!");
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-1">Settlement Batch Upload</h1>
          <p className="text-gray-600">Upload a CSV file with settlement payment details for async batch processing</p>
        </div>
        <button
          onClick={() => navigate("/settlement-list")}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border rounded hover:bg-gray-100"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Bact to Settlement List
        </button>
      </div>

      {/* 2-column layout */}
      <div className="flex flex-col lg:flex-row gap-6">

        {/* LEFT: CSV Format Instructions */}
        <div className="lg:w-2/5 flex-shrink-0">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-start justify-between mb-4 gap-3">
              <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faFileAlt} className="text-blue-600" />
                CSV File Format Instructions
              </h3>
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg whitespace-nowrap transition-colors"
              >
                <FontAwesomeIcon icon={faDownload} />
                Download Template
              </button>
            </div>

            <div className="space-y-2 mb-5">
              {CSV_COLUMNS.map((col, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-2 p-2.5 rounded-lg border ${
                    col.required ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="flex-shrink-0 mt-1.5">
                    <span className={`inline-block w-2 h-2 rounded-full ${col.required ? "bg-red-500" : "bg-gray-400"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm font-semibold ${col.required ? "text-red-700" : "text-gray-700"}`}>
                      {col.label}
                    </span>
                    {col.required && (
                      <span className="ml-1.5 text-xs font-bold text-red-600 uppercase tracking-wide">*required</span>
                    )}
                    <p className="text-xs text-gray-500 mt-0.5">{col.note}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs font-bold text-yellow-800 mb-1.5 flex items-center gap-1">
                <FontAwesomeIcon icon={faExclamationTriangle} /> Notes
              </p>
              <ul className="text-xs text-yellow-800 space-y-1 list-disc list-inside">
                <li>All red-marked fields are required per row</li>
                <li>Dates must be in DD/MM/YYYY format</li>
                <li>Amounts can include commas (e.g. 1,500.00)</li>
                <li>Maximum file size: 10 MB</li>
                <li>Processing is async — check Upload History for status</li>
              </ul>
            </div>
          </div>
        </div>

        {/* RIGHT: Drag & Drop Upload */}
        <div className="lg:flex-1">
          <div className="bg-white rounded-lg shadow-md p-6 mb-5">
            <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faCloudUploadAlt} className="text-blue-600" />
              Upload CSV File
            </h3>

            {/* Drop zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
                isDragOver ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <FontAwesomeIcon
                icon={faCloudUploadAlt}
                className={`text-5xl mb-3 ${isDragOver ? "text-blue-600" : "text-blue-400"}`}
              />
              <h4 className="text-base font-semibold text-gray-700 mb-1">Drag and drop your CSV file here</h4>
              <p className="text-sm text-gray-500 mb-3">or click to browse</p>
              <input ref={fileInputRef} type="file" accept=".csv,.txt" onChange={handleFileSelect} className="hidden" />
              <p className="text-xs text-gray-400">Accepted: .csv &mdash; Max size: 10 MB</p>
            </div>

            {/* Selected file info */}
            {file && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">{file.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{(file.size / 1024).toFixed(2)} KB</p>
                </div>
                <button
                  onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  title="Remove file"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            )}

            {/* Action buttons */}
            {file && (
              <div className="mt-4 flex gap-3">
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faUpload} className={uploading ? "animate-spin" : ""} />
                  {uploading ? "Uploading..." : "Upload & Process"}
                </button>
                <button
                  onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                  disabled={uploading}
                  className="px-4 py-2.5 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faTimes} />
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* Upload result */}
          {uploadResult && (
            <div className={`rounded-lg p-5 ${uploadResult.success ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
              <div className="flex items-start gap-3">
                <FontAwesomeIcon
                  icon={uploadResult.success ? faCheckCircle : faTimesCircle}
                  className={`text-2xl flex-shrink-0 mt-0.5 ${uploadResult.success ? "text-green-600" : "text-red-600"}`}
                />
                <div className="flex-1">
                  <h4 className={`text-base font-bold mb-1 ${uploadResult.success ? "text-green-800" : "text-red-800"}`}>
                    {uploadResult.success ? "Upload Successful!" : "Upload Failed"}
                  </h4>
                  <p className={`text-sm ${uploadResult.success ? "text-green-700" : "text-red-700"}`}>
                    {typeof uploadResult.message === "string" ? uploadResult.message : "An error occurred"}
                  </p>

                  {uploadResult.success && uploadResult.uploadId && (
                    <>
                      <div className="mt-3 p-2.5 bg-white rounded border border-green-200">
                        <p className="text-xs text-gray-500 mb-0.5">Upload ID:</p>
                        <p className="font-mono text-xs text-gray-800 break-all">{uploadResult.uploadId}</p>
                        <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                          <FontAwesomeIcon icon={faInfoCircle} />
                          Processing in background. Use the buttons below to track progress.
                        </p>
                      </div>

                      <div className="mt-4 flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => navigate(`/settlement-batch-upload-processed/${uploadResult.uploadId}`)}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <FontAwesomeIcon icon={faEye} />
                          View Processed
                        </button>
                        <button
                          onClick={() => navigate("/settlement-batch-upload-history")}
                          className="flex-1 bg-gray-700 hover:bg-gray-800 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <FontAwesomeIcon icon={faHistory} />
                          View History
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default SettlementBatchUploadPage;
